/** Shared History / Transaction Reports module metadata (aligns with backend report columns). */

/** Investor sidebar and page title (route stays /my-history). */
export const INVESTOR_HISTORY_PAGE_TITLE = "Transaction Reports";

export const HISTORY_SECTIONS = ["deposits", "withdrawals", "transactions", "redemptions", "transfers"];

/** Investor-only tab: live portfolio holdings (not a ledger export section). */
export const INVESTOR_HOLDINGS_SECTION = "holdings";

/** Icons for Transaction Reports section tabs (InvestorSectionIcon keys). */
export const HISTORY_SECTION_ICONS = {
  deposits: "deposit",
  withdrawals: "withdraw",
  transactions: "transfer",
  redemptions: "redeemable",
  transfers: "transfer",
  [INVESTOR_HOLDINGS_SECTION]: "holdings",
  "wallet-activity": "wallet-activity",
};

export function historySectionIcon(sectionId) {
  return HISTORY_SECTION_ICONS[sectionId] || null;
}

/** Investor My History: extra tabs that route outside /my-history (e.g. wallet ledger). */
export const INVESTOR_HISTORY_EXTERNAL_TABS = [
  { insertAfter: "withdrawals", id: "wallet-activity", label: "Wallet activity", to: "/transactions" },
];

/** Admin-only History tab (platform fee income). */
export const ADMIN_ONLY_HISTORY_SECTIONS = ["platform-fees"];

export const ADMIN_HISTORY_SECTIONS = [...HISTORY_SECTIONS, ...ADMIN_ONLY_HISTORY_SECTIONS];

export const SECTION_META = {
  deposits: {
    id: "deposits",
    labelAdmin: "Deposits",
    labelInvestor: "My deposits",
    hasStatusFilter: true,
    statusOptions: [
      { value: "", label: "Any status" },
      { value: "PENDING", label: "Pending" },
      { value: "APPROVED", label: "Approved" },
      { value: "REJECTED", label: "Rejected" },
    ],
    hasProjectFilter: false,
    hasMovementFilter: false,
  },
  withdrawals: {
    id: "withdrawals",
    labelAdmin: "Withdrawals",
    labelInvestor: "My withdrawals",
    hasStatusFilter: true,
    statusOptions: [
      { value: "", label: "Any status" },
      { value: "PENDING", label: "Pending" },
      { value: "PROCESSING", label: "Processing" },
      { value: "COMPLETED", label: "Paid out" },
      { value: "FAILED", label: "Failed" },
      { value: "REJECTED", label: "Rejected" },
    ],
    hasProjectFilter: false,
    hasMovementFilter: false,
  },
  transactions: {
    id: "transactions",
    labelAdmin: "Transactions",
    labelInvestor: "My transactions",
    hasStatusFilter: false,
    statusOptions: [],
    hasProjectFilter: true,
    hasMovementFilter: true,
  },
  redemptions: {
    id: "redemptions",
    labelAdmin: "Redemptions",
    labelInvestor: "My redemptions",
    hasStatusFilter: true,
    statusOptions: [
      { value: "", label: "Any status" },
      { value: "PENDING", label: "Pending" },
      { value: "APPROVED", label: "Approved" },
      { value: "REJECTED", label: "Rejected" },
      { value: "COMPLETED", label: "Fulfilled" },
    ],
    hasProjectFilter: true,
    hasMovementFilter: false,
  },
  "platform-fees": {
    id: "platform-fees",
    labelAdmin: "Platform income (fees)",
    labelInvestor: "Platform income (fees)",
    hasStatusFilter: true,
    statusOptions: [
      { value: "", label: "All fee types" },
      { value: "TRANSFER", label: "Transfer fees" },
      { value: "REDEMPTION", label: "Redemption fees" },
    ],
    hasProjectFilter: true,
    hasMovementFilter: false,
  },
  holdings: {
    id: "holdings",
    labelAdmin: "Holdings",
    labelInvestor: "My Holdings",
    hasStatusFilter: false,
    statusOptions: [],
    hasProjectFilter: false,
    hasMovementFilter: false,
  },
  transfers: {
    id: "transfers",
    labelAdmin: "Transfers",
    labelInvestor: "My transfers",
    hasStatusFilter: true,
    statusOptions: [
      { value: "", label: "Any status" },
      { value: "PENDING", label: "Pending" },
      { value: "AWAITING_RECEIVER", label: "Awaiting receiver" },
      { value: "APPROVED", label: "Approved" },
      { value: "REJECTED", label: "Rejected" },
    ],
    hasProjectFilter: true,
    hasMovementFilter: false,
  },
};

export const MOVEMENT_OPTIONS = [
  { value: "", label: "All types" },
  { value: "INVESTMENT", label: "Share purchased" },
  { value: "FEE", label: "Fee" },
  { value: "REDEMPTION", label: "Redemption" },
  { value: "TRANSFER", label: "Transfer" },
  { value: "WALLET_ADJUSTMENT", label: "Wallet adjustment" },
];

export function normalizeHistorySection(raw, { isAdmin = false } = {}) {
  const s = String(raw || "").toLowerCase();
  if (!isAdmin && s === INVESTOR_HOLDINGS_SECTION) return INVESTOR_HOLDINGS_SECTION;
  const allowed = isAdmin ? ADMIN_HISTORY_SECTIONS : HISTORY_SECTIONS;
  return allowed.includes(s) ? s : "deposits";
}

export function investorHistorySections() {
  return [...HISTORY_SECTIONS, INVESTOR_HOLDINGS_SECTION];
}

/** Section tabs plus external links for investor My History navigation. */
export function getInvestorHistoryTabItems() {
  const items = [];
  for (const id of HISTORY_SECTIONS) {
    items.push({ type: "section", id });
    for (const link of INVESTOR_HISTORY_EXTERNAL_TABS) {
      if (link.insertAfter === id) {
        items.push({ type: "link", id: link.id, label: link.label, to: link.to });
      }
    }
  }
  items.push({ type: "section", id: INVESTOR_HOLDINGS_SECTION });
  return items;
}

export function getColumnsForSection(section, { isAdmin = false } = {}) {
  const s = normalizeHistorySection(section, { isAdmin });
  if (s === "platform-fees") {
    return {
      keys: [
        "sl",
        "fee_category",
        "reference_id",
        "project_name",
        "counterparty",
        "fee_cash_bdt",
        "fee_share_bdt",
        "fee_total_bdt",
        "fee_shares_to_ssa",
        "fee_shares_burned",
        "settlement",
        "date",
      ],
      labels: {
        sl: "SL",
        fee_category: "Type",
        reference_id: "Reference",
        project_name: "Project",
        counterparty: "Paid by",
        fee_cash_bdt: "Cash (BDT)",
        fee_share_bdt: "Share fee (BDT)",
        fee_total_bdt: "Total (BDT)",
        fee_shares_to_ssa: "Shares to SSA",
        fee_shares_burned: "Shares burned (legacy)",
        settlement: "Settlement",
        date: "Date",
      },
    };
  }
  if (s === "deposits") {
    return {
      keys: [
        "sl",
        "deposit_id",
        "investor_name",
        "project",
        "amount",
        "payment_method",
        "status",
        "request_date",
        "approval_date",
        "remarks",
        "admin_remark",
      ],
      labels: {
        sl: "SL",
        deposit_id: "Deposit ID",
        investor_name: "Investor",
        project: "Project",
        amount: "Amount",
        payment_method: "Method",
        status: "Status",
        request_date: "Requested",
        approval_date: "Approved",
        remarks: "Rejection reason",
        admin_remark: "Admin note",
      },
    };
  }
  if (s === "withdrawals") {
    return {
      keys: [
        "sl",
        "withdrawal_id",
        "investor_name",
        "amount",
        "fee_deducted",
        "net_amount",
        "status",
        "request_date",
        "approval_date",
        "remarks",
      ],
      labels: {
        sl: "SL",
        withdrawal_id: "Withdrawal ID",
        investor_name: "Investor",
        amount: "Amount",
        fee_deducted: "Fee",
        net_amount: "Net",
        status: "Status",
        request_date: "Requested",
        approval_date: "Settled",
        remarks: "Remarks",
      },
    };
  }
  if (s === "transactions") {
    return {
      keys: [
        "sl",
        "transaction_id",
        "investor_name",
        "project_name",
        "type",
        "shares",
        "debit",
        "credit",
        "balance_after_transaction",
        "date",
        "reference_note",
      ],
      labels: {
        sl: "SL",
        transaction_id: "Transaction ID",
        investor_name: "Investor",
        project_name: "Project",
        type: "Type",
        shares: "Shares",
        debit: "Debit",
        credit: "Credit",
        balance_after_transaction: "Balance after",
        date: "Date",
        reference_note: "Reference",
      },
    };
  }
  if (s === "redemptions") {
    return {
      keys: [
        "sl",
        "redemption_id",
        "investor_name",
        "project_name",
        "shares",
        "nav_request_time",
        "nav_approval_time",
        "gross_amount",
        "fee_breakdown",
        "net_payout",
        "status",
        "request_date",
        "approval_date",
      ],
      labels: {
        sl: "SL",
        redemption_id: "Redemption ID",
        investor_name: "Investor",
        project_name: "Project",
        shares: "Shares",
        nav_request_time: "NAV (req)",
        nav_approval_time: "NAV (appr)",
        gross_amount: "Gross",
        fee_breakdown: "Fees",
        net_payout: "Net payout",
        status: "Status",
        request_date: "Requested",
        approval_date: "Processed",
      },
    };
  }
  return {
    keys: [
      "sl",
      "transfer_id",
      "from_investor",
      "to_investor",
      "project_name",
      "shares_transferred",
      "transfer_fee_bdt",
      "transfer_type",
      "date",
      "remarks",
    ],
    labels: {
      sl: "SL",
      transfer_id: "Transfer ID",
      from_investor: "From",
      to_investor: "To / Treasury",
      project_name: "Project",
      shares_transferred: "Shares",
      transfer_fee_bdt: "Transfer fee (BDT)",
      transfer_type: "Type",
      date: "Date",
      remarks: "Status",
    },
  };
}

/** Human-readable summary card copy per API summary key. */
export const SUMMARY_CARD_LABELS = {
  count: "Rows",
  total_amount: "Total",
  total_credit: "Credits",
  total_debit: "Debits",
  total_net_payout: "Net (completed)",
  total_shares: "Shares",
  total_fee_cash_bdt: "Cash fees (BDT)",
  total_fee_share_bdt: "Share fees (BDT value)",
  total_platform_fee_bdt: "Total platform fees (BDT)",
  company_revenue_wallet_bdt: "Revenue wallet balance",
};
